/* eslint-disable */
/**
 * native-p2p.provider.js
 * Provedor de Realtime P2P Nativo Cross-Platform (BLE / Hotspot local).
 * Opera em conjunto com o plugin Capacitor 'TriadLink' e provê transporte direto sem internet.
 * Inclui auto-reconexão com backoff e sincronização contínua de estado (resync pós-desbloqueio).
 */

import { TriadLink } from '@/native/triad-link';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

class NativeP2PRealtimeProvider {
    constructor() {
        this.role = 'idle'; // 'host' | 'guest' | 'idle'
        this.status = 'DISCONNECTED';
        this.activePin = null;
        this.sessionActive = false;
        this.connectedPeers = new Map(); // peerId -> { mtu, connectedAt }
        this.channels = {}; // channelName -> Array of handlers
        this.statusListeners = {}; // channelName -> callback
        this.msgSeq = 0;
        this.myPeerId = 'native_' + Math.random().toString(36).substring(2, 9);
        
        // Desfragmentação de mensagens recebidas: `${peerId}:${msgSeq}` -> { total, received, parts: [], timer }
        this.incomingChunks = new Map();

        // Auto-reconexão e ciclo de vida
        this.reconnectTimer = null;
        this.reconnectAttempts = 0;
        this.isReconnecting = false;
        this.stateProvider = null; // Função do Host que retorna { songId, playlistId, key, bpm }

        // Listeners do plugin nativo
        this.pluginListeners = [];
        this.isInitialized = false;
    }

    async init() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        try {
            const l1 = await TriadLink.addListener('peerConnected', (event) => {
                console.log('[NativeP2P] Peer conectado:', event);
                this.clearReconnectTimer();
                this.reconnectAttempts = 0;
                this.connectedPeers.set(event.peerId, { mtu: event.mtu || 512, connectedAt: Date.now() });
                this.updateOverallStatus();
                // Guest conectado pede sincronização imediata do estado
                if (this.role === 'guest') {
                    this.requestStateSync();
                }
            });
            this.pluginListeners.push(l1);

            const l2 = await TriadLink.addListener('peerDisconnected', (event) => {
                console.log('[NativeP2P] Peer desconectado:', event);
                this.connectedPeers.delete(event.peerId);
                this.updateOverallStatus();

                // Se a sessão estiver ativa e formos Guest, agenda reconexão automática
                if (this.sessionActive && this.role === 'guest') {
                    this.scheduleReconnect();
                }
            });
            this.pluginListeners.push(l2);

            const l3 = await TriadLink.addListener('frame', (event) => {
                this.handleIncomingFrame(event.peerId, event.data);
            });
            this.pluginListeners.push(l3);

            const l4 = await TriadLink.addListener('stateChanged', (event) => {
                console.log('[NativeP2P] Estado do rádio alterado:', event);
                if (event.state === 'error') {
                    this.updateOverallStatus('CHANNEL_ERROR');
                }
            });
            this.pluginListeners.push(l4);
        } catch (e) {
            console.warn('[NativeP2P] Falha ao registrar listeners do plugin nativo:', e);
        }

        // Listener de retorno do App (App resume / tela desbloqueada)
        try {
            if (Capacitor.isNativePlatform()) {
                App.addListener('appStateChange', (state) => {
                    if (state && state.isActive) {
                        this.handleAppResume();
                    }
                });
            }
        } catch (e) {
            console.warn('[NativeP2P] Falha ao registrar appStateChange:', e);
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.handleAppResume();
                }
            });
        }
    }

    async handleAppResume() {
        if (!this.sessionActive) return;
        console.log('[NativeP2P] App voltou para primeiro plano (sessionActive=true, role=' + this.role + ')');

        if (this.role === 'guest') {
            if (this.connectedPeers.size === 0) {
                console.log('[NativeP2P] Guest desconectado ao voltar para o app. Iniciando auto-reconexão imediata...');
                await this.reconnectGuest();
            } else {
                console.log('[NativeP2P] Guest conectado ao voltar para o app. Solicitando sync-request...');
                this.requestStateSync();
            }
        } else if (this.role === 'host' && this.activePin) {
            // Garante que o Advertising do Host permanece ativo
            try {
                const serviceUuid = this.deriveServiceUuid(this.activePin);
                await TriadLink.startHost({
                    serviceUuid,
                    deviceName: 'Triad Leader',
                    maxPeers: 6
                });
            } catch (e) {
                console.warn('[NativeP2P] Re-anunciar host no resume:', e);
            }
        }
    }

    scheduleReconnect(delayMs = 1500) {
        if (!this.sessionActive || this.role !== 'guest' || this.isReconnecting) return;
        this.clearReconnectTimer();

        this.reconnectTimer = setTimeout(() => {
            this.reconnectGuest();
        }, delayMs);
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    async reconnectGuest() {
        if (!this.sessionActive || this.role !== 'guest' || !this.activePin) return;
        if (this.connectedPeers.size > 0) return;

        this.isReconnecting = true;
        try {
            console.log('[NativeP2P] Tentando auto-reconexão com o Líder PIN:', this.activePin);
            const serviceUuid = this.deriveServiceUuid(this.activePin);
            await TriadLink.startGuest({
                serviceUuid,
                deviceName: 'Triad Musician'
            });
        } catch (e) {
            console.warn('[NativeP2P] Erro na tentativa de reconexão:', e);
        } finally {
            this.isReconnecting = false;
            // Se ainda não conectou, agenda nova tentativa com backoff leve
            if (this.sessionActive && this.connectedPeers.size === 0) {
                this.reconnectAttempts++;
                const nextDelay = Math.min(1500 + this.reconnectAttempts * 500, 4000);
                this.scheduleReconnect(nextDelay);
            } else {
                this.reconnectAttempts = 0;
            }
        }
    }

    setStateProvider(providerFn) {
        this.stateProvider = providerFn;
    }

    nextMessageId() {
        this.msgSeq += 1;
        return `${this.myPeerId}:${Date.now()}:${this.msgSeq}`;
    }

    async isSupported() {
        try {
            const res = await TriadLink.isAvailable();
            return !!res?.available;
        } catch (e) {
            return false;
        }
    }

    async ensurePermissions() {
        try {
            if (typeof TriadLink.requestPermissions === 'function') {
                const res = await TriadLink.requestPermissions();
                if (res && res.granted === false) {
                    console.warn('[NativeP2P] Permissões de rádio não concedidas:', res);
                }
            }
        } catch (e) {
            console.warn('[NativeP2P] Erro ao requisitar permissões de rádio:', e);
        }
    }

    /**
     * Inicia sessão Host (Líder) anunciando PIN de 6 dígitos
     */
    async startHost(pin) {
        await this.init();
        await this.ensurePermissions();
        const cleanPin = String(pin || '').trim();
        if (cleanPin.length !== 6) {
            throw new Error('PIN deve conter 6 dígitos');
        }
        this.role = 'host';
        this.activePin = cleanPin;
        this.sessionActive = true;
        this.clearReconnectTimer();

        // Deriva service UUID identificador da sessão
        const serviceUuid = this.deriveServiceUuid(cleanPin);
        await TriadLink.startHost({
            serviceUuid,
            deviceName: 'Triad Leader',
            maxPeers: 6
        });
        return cleanPin;
    }

    /**
     * Inicia sessão Guest (Músico) procurando o Host pelo PIN de 6 dígitos
     */
    async joinByPin(pin, timeoutMs = 25000) {
        await this.init();
        await this.ensurePermissions();
        const cleanPin = String(pin || '').trim();
        if (cleanPin.length !== 6) {
            throw new Error('PIN deve conter 6 dígitos');
        }
        this.role = 'guest';
        this.activePin = cleanPin;
        this.sessionActive = true;
        this.clearReconnectTimer();

        const serviceUuid = this.deriveServiceUuid(cleanPin);

        const waitForConnection = new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                clearInterval(checkInterval);
                reject(new Error('Líder não encontrado via rádio. Verifique se o líder iniciou a sessão e se o PIN está correto.'));
            }, timeoutMs);

            const check = () => {
                if (this.connectedPeers.size > 0) {
                    clearTimeout(timer);
                    clearInterval(checkInterval);
                    resolve(true);
                    return true;
                }
                return false;
            };

            if (check()) return;

            const checkInterval = setInterval(() => {
                check();
            }, 300);
        });

        await TriadLink.startGuest({
            serviceUuid,
            deviceName: 'Triad Musician'
        });

        await waitForConnection;
        return true;
    }

    deriveServiceUuid(pin) {
        // Converte o PIN numérico (até 999999) em hexadecimal de 6 dígitos e usa vendor GUID próprio
        // para evitar colisão com Bluetooth SIG Base UUID e compressão para 32-bit no Android 12+
        // (ex: fa03e64a-3b1a-4d92-bc10-721a9c3d4e5f)
        const num = parseInt(pin, 10);
        const hexPin = (isNaN(num) ? 0 : num).toString(16).padStart(6, '0');
        return `fa${hexPin}-3b1a-4d92-bc10-721a9c3d4e5f`;
    }

    /**
     * Tratamento de fragmentação e recepção de frames
     */
    handleIncomingFrame(peerId, frameData) {
        try {
            const raw = typeof frameData === 'string' ? JSON.parse(frameData) : frameData;
            
            // Suporte a mensagens fragmentadas: { _seq, _idx, _tot, _payload }
            if (raw._tot && raw._tot > 1) {
                const chunkKey = `${peerId}:${raw._seq}`;
                let record = this.incomingChunks.get(chunkKey);
                if (!record) {
                    record = {
                        total: raw._tot,
                        received: 0,
                        parts: new Array(raw._tot),
                        timer: setTimeout(() => this.incomingChunks.delete(chunkKey), 5000)
                    };
                    this.incomingChunks.set(chunkKey, record);
                }
                if (!record.parts[raw._idx]) {
                    record.parts[raw._idx] = raw._payload;
                    record.received += 1;
                }
                if (record.received === record.total) {
                    clearTimeout(record.timer);
                    this.incomingChunks.delete(chunkKey);
                    const assembledStr = record.parts.join('');
                    this.routeIncomingMessage(JSON.parse(assembledStr));
                }
                return;
            }

            // Mensagem completa em um único frame
            this.routeIncomingMessage(raw);
        } catch (e) {
            console.warn('[NativeP2P] Falha ao processar frame recebido:', e);
        }
    }

    routeIncomingMessage(message) {
        const { channelName, eventName, data } = message;
        if (!channelName) return;

        // Se o Líder recebe de um membro, faz relay para os outros membros
        if (this.role === 'host') {
            this.broadcastToGuests(message, message._sourcePeerId);
        }

        // Protocolo de sincronização de estado:
        // Host responde a sync-request enviando sync-state
        if (channelName === 'stage-sync') {
            if (eventName === 'sync-request' && this.role === 'host') {
                console.log('[NativeP2P] Host recebeu sync-request, respondendo com sync-state...');
                if (typeof this.stateProvider === 'function') {
                    try {
                        const state = this.stateProvider();
                        if (state) {
                            this.publish('stage-sync', 'sync-state', state).catch(() => {});
                        }
                    } catch (e) {
                        console.warn('[NativeP2P] Erro ao obter estado do stateProvider:', e);
                    }
                }
            }
        }

        // 1. Ouvintes do canal exato
        let listeners = [...(this.channels[channelName] || [])];

        // 2. Fallback de palco: se for evento de playlist (ex: change-song) e o Guest estiver
        // em outra playlist aberta ou nenhuma específica, entrega para ouvintes de qualquer playlist room
        if (channelName.startsWith('playlist::room::')) {
            Object.keys(this.channels).forEach(ch => {
                if (ch.startsWith('playlist::room::') && ch !== channelName) {
                    listeners.push(...this.channels[ch]);
                }
            });
        }

        if (listeners.length === 0) return;

        listeners.forEach(handler => {
            try {
                const mid = (data && typeof data === 'object' && data.__mid) ? data.__mid : (message.mid || null);
                const payload = { ...data, event: eventName, __mid: mid };
                handler(payload);
            } catch (err) {
                console.error('[NativeP2P] Erro no listener do canal:', err);
            }
        });
    }

    requestStateSync() {
        console.log('[NativeP2P] Enviando sync-request para o Líder...');
        this.publish('stage-sync', 'sync-request', { requestedAt: Date.now() }).catch(() => {});
    }

    updateOverallStatus(explicitStatus = null) {
        if (explicitStatus) {
            this.status = explicitStatus;
        } else {
            this.status = this.connectedPeers.size > 0 ? 'SUBSCRIBED' : 'DISCONNECTED';
        }

        Object.values(this.statusListeners).forEach(cb => {
            if (typeof cb === 'function') {
                cb(this.status, null);
            }
        });
    }

    subscribe(channelName, eventName, onMessage, onStatusChange) {
        if (!this.channels[channelName]) {
            this.channels[channelName] = [];
        }

        const handler = (payload) => {
            if (eventName === '*' || payload.event === eventName) {
                onMessage(payload);
            }
        };

        this.channels[channelName].push(handler);

        if (onStatusChange) {
            this.statusListeners[channelName] = onStatusChange;
            onStatusChange(this.status, null);
        }

        return {
            unsubscribe: () => this.unsubscribe(channelName)
        };
    }

    unsubscribe(channelName) {
        delete this.channels[channelName];
        delete this.statusListeners[channelName];
    }

    async sendData(jsonStr) {
        // Limite seguro: 180 bytes de payload + wrapper e quotes escaping fica ~240 bytes,
        // bem abaixo do limite estrito de 512 bytes de atributo GATT do Android/iOS.
        const CHUNK_SIZE = 180;
        if (jsonStr.length <= CHUNK_SIZE) {
            await TriadLink.send({ data: jsonStr });
            return;
        }

        const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE);
        const seq = ++this.msgSeq;
        for (let i = 0; i < totalChunks; i++) {
            const chunkPart = jsonStr.substr(i * CHUNK_SIZE, CHUNK_SIZE);
            const frame = {
                _seq: seq,
                _idx: i,
                _tot: totalChunks,
                _payload: chunkPart
            };
            await TriadLink.send({ data: JSON.stringify(frame) });
            // Pequeno intervalo entre pacotes BLE para a controladora de rádio não afogar o buffer
            if (i < totalChunks - 1) {
                await new Promise(r => setTimeout(r, 25));
            }
        }
    }

    async publish(channelName, eventName, data) {
        if (this.connectedPeers.size === 0) {
            return { sentCount: 0, skipped: true };
        }

        const mid = (data && typeof data === 'object' && data.__mid) ? data.__mid : this.nextMessageId();
        const payload = {
            channelName,
            eventName,
            data,
            mid,
            _sourcePeerId: this.myPeerId,
            timestamp: Date.now()
        };

        const jsonStr = JSON.stringify(payload);
        await this.sendData(jsonStr);
        return { sentCount: this.connectedPeers.size };
    }

    broadcastToGuests(payload, excludePeerId) {
        const jsonStr = JSON.stringify(payload);
        this.sendData(jsonStr).catch(() => {});
    }

    getConnectedCount() {
        return this.connectedPeers.size;
    }

    async disconnect() {
        this.sessionActive = false;
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        try {
            await TriadLink.stop();
        } catch (e) {}
        this.connectedPeers.clear();
        this.role = 'idle';
        this.activePin = null;
        this.updateOverallStatus('DISCONNECTED');
    }
}

export const nativeP2PService = new NativeP2PRealtimeProvider();
export default nativeP2PService;
